import React, { useState } from 'react';
import moment from 'moment';
import { Upload, Check, X, Database, Trash2 } from 'lucide-react';
import { useSocket } from '../Context/SocketContext';
import { infoUser } from '../utils/auth';

const FIELDS = [
    { key: 'email', label: 'Email', guess: ['email', 'mail'] },
    { key: 'name', label: 'Nom', guess: ['name', 'nom', 'client'] },
    { key: 'date', label: 'Date', guess: ['created', 'date'] },
    { key: 'amount', label: 'Total dépensé', guess: ['total spend', 'total', 'montant', 'amount', 'spend'] },
    { key: 'count', label: 'Nb paiements', guess: ['payment count', 'count', 'nombre'] },
]

const MONTH_PRICE = { 1: 10, 3: 25.99, 6: 49.99 }
const CURRENCY_MAP = {
    PLN: { 43.74: 1, 113.68: 3, 218.65: 6 },
    EUR: { 10: 1, 25.99: 3, 49.99: 6 },
}
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100
const amountToMonths = (amount, currency) => {
    const m = CURRENCY_MAP[currency] || {}
    let best = 1, bestDiff = Infinity
    Object.entries(m).forEach(([amt, mo]) => {
        const diff = Math.abs(parseFloat(amt) - amount)
        if (diff < bestDiff) { bestDiff = diff; best = mo }
    })
    return best
}
const monthsToPrice = (months) => MONTH_PRICE[months] ?? 0
const parseDate = (d) => moment(d, ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD', moment.ISO_8601], false)
const emailKey = (e) => (e || '').trim().toLowerCase()

const UserBackupImport = () => {
    const { socket } = useSocket()
    const userToken = infoUser()

    const [headers, setHeaders] = useState([])
    const [rawRows, setRawRows] = useState([])
    const [files, setFiles] = useState([])
    const [map, setMap] = useState({ email: '', name: '', date: '', amount: '', count: '' })
    const [pending, setPending] = useState(null)
    const [deselected, setDeselected] = useState({})
    const [status, setStatus] = useState({})
    const [saving, setSaving] = useState(false)
    const [saved, setSaved] = useState(0)

    const parseLine = (line, delim) => {
        const out = []; let cur = ''; let inQ = false
        for (let i = 0; i < line.length; i++) {
            const c = line[i]
            if (c === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++ } else inQ = !inQ }
            else if (c === delim && !inQ) { out.push(cur); cur = '' }
            else cur += c
        }
        out.push(cur)
        return out.map(s => s.trim())
    }
    const parseCsv = (text) => {
        const lines = text.split(/\r?\n/).filter(l => l.trim() !== '')
        if (!lines.length) return { headers: [], rows: [] }
        const delim = (lines[0].split(';').length > lines[0].split(',').length) ? ';' : ','
        const hs = parseLine(lines[0], delim)
        const rs = lines.slice(1).map(l => {
            const cells = parseLine(l, delim)
            const obj = {}; hs.forEach((h, i) => { obj[h] = cells[i] ?? '' }); return obj
        })
        return { headers: hs, rows: rs }
    }
    const matchHeader = (h, g) => new RegExp('\\b' + g.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'i').test(h)
    const guessMap = (hs) => {
        const out = {}
        FIELDS.forEach(f => { out[f.key] = hs.find(h => f.guess.some(g => matchHeader(h, g))) || '' })
        return out
    }

    const onFiles = (e) => {
        const f = e.currentTarget.files?.[0]
        if (f) setPending(f)
        e.currentTarget.value = ''
    }
    const confirmCurrency = (cur) => {
        const file = pending
        setPending(null)
        if (!file) return
        const reader = new FileReader()
        reader.onload = (ev) => {
            const { headers: hs, rows } = parseCsv(String(ev.target.result))
            const tagged = rows.map(r => ({ ...r, __currency: cur }))
            const newHeaders = headers.length ? headers : hs
            const allRaw = [...rawRows, ...tagged]
            const mapping = (map.email || map.amount) ? map : guessMap(newHeaders)
            setHeaders(newHeaders); setRawRows(allRaw); setMap(mapping)
            setFiles(fs => [...fs, `${file.name} · ${cur} (${rows.length})`])
            setStatus({})
        }
        reader.readAsText(file)
    }
    const reset = () => {
        setRawRows([]); setFiles([]); setHeaders([]); setDeselected({}); setStatus({}); setSaved(0)
        setMap({ email: '', name: '', date: '', amount: '', count: '' })
    }

    const eventOf = (r) => {
        const cur = r.__currency || 'EUR'
        const total = parseFloat(String(map.amount ? r[map.amount] : '').replace(',', '.')) || 0
        const count = parseInt(map.count ? r[map.count] : '') || 1
        const per = count > 0 ? round2(total / count) : total
        const monthsUnit = amountToMonths(per, cur)
        return { date: parseDate(map.date ? r[map.date] : ''), months: monthsUnit * count, euro: monthsToPrice(monthsUnit) * count, count }
    }

    const groups = {}
    rawRows.forEach(r => {
        const email = map.email ? r[map.email] : ''
        const key = emailKey(email); if (!key) return
        if (!groups[key]) groups[key] = { email, name: '', events: [] }
        const nm = map.name ? r[map.name] : ''
        if (nm && !groups[key].name) groups[key].name = nm
        groups[key].events.push(eventOf(r))
    })

    const merged = Object.values(groups).map(g => {
        const dated = g.events.filter(e => e.date && e.date.isValid()).sort((a, b) => a.date.valueOf() - b.date.valueOf())
        const undated = g.events.filter(e => !(e.date && e.date.isValid()))
        let end = null, months = 0, euro = 0, count = 0
        dated.forEach(e => {
            const base = (end && end.isAfter(e.date)) ? end : e.date.clone()
            end = base.clone().add(e.months, 'months')
            months += e.months; euro += e.euro; count += e.count
        })
        undated.forEach(e => { months += e.months; euro += e.euro; count += e.count; if (end) end = end.clone().add(e.months, 'months') })
        const first = dated.length ? dated[0].date : null
        const premium = end ? end.isAfter(moment()) : false
        const daysLeft = end ? end.diff(moment(), 'days') : 0
        let remaining = 'Expiré'
        if (premium) remaining = daysLeft >= 60 ? `${Math.round(daysLeft / 30)} mois` : (daysLeft >= 1 ? `${daysLeft} j` : '< 1 j')
        return {
            email: g.email, name: g.name, count, euro: round2(euro), months,
            firstStr: first && first.isValid() ? first.format('YYYY-MM-DD HH:mm:ss') : '',
            end, endStr: end && end.isValid() ? end.format('YYYY-MM-DD HH:mm:ss') : '',
            premium, remaining,
        }
    }).sort((a, b) => Number(b.premium) - Number(a.premium) || b.euro - a.euro)

    const isSel = (email) => !deselected[emailKey(email)]
    const toggleSel = (email) => setDeselected(s => ({ ...s, [emailKey(email)]: isSel(email) }))
    const allSelected = merged.length > 0 && merged.every(m => isSel(m.email))
    const toggleAll = () => setDeselected(allSelected ? merged.reduce((a, m) => { a[emailKey(m.email)] = true; return a }, {}) : {})
    const selectedCount = merged.filter(m => isSel(m.email)).length

    const emitMerged = (m) => new Promise((resolve) => {
        socket.emit('empire_add_user_backup_pay', {
            token: userToken.expecttoken, email: m.email, name: m.name, price: m.euro,
            datepay: m.firstStr, dateend: m.premium ? m.endStr : null, months: m.months, count: m.count,
        }, (ok) => resolve(!!ok))
    })
    const saveSelected = async () => {
        setSaving(true); setSaved(0)
        let c = 0
        for (const m of merged) {
            if (!isSel(m.email)) continue
            const key = emailKey(m.email)
            setStatus(s => ({ ...s, [key]: 'loading' }))
            const ok = await emitMerged(m)
            setStatus(s => ({ ...s, [key]: ok ? 'ok' : 'err' }))
            if (ok) { c++; setSaved(c) }
        }
        setSaving(false)
    }

    return (
        <>
            <div className='user-backup'>
                <div className='df aic jcsb gap1 mb2'>
                    <div className='df aic gap1'>
                        <Database size={20} color='#fff' />
                        <p className='fb cw fs18'>Import backup paiements</p>
                    </div>
                    {rawRows.length > 0 && (
                        <button onClick={reset} className='pay-act-btn' title='Réinitialiser'><Trash2 size={16} color='#f71717' /></button>
                    )}
                </div>

                <label className='ub-drop df fdc aic jcc gap1'>
                    <Upload size={26} color='#9754e3' />
                    <span className='cw fr'>Ajouter un CSV</span>
                    <span className='cw fr fs12' style={{ opacity: 0.5 }}>la devise sera demandée à chaque fichier</span>
                    <input type='file' accept='.csv,text/csv' onChange={onFiles} hidden />
                </label>

                {files.length > 0 && (
                    <div className='df fw gap1 mt1'>{files.map((f, i) => <span key={i} className='ub-file'>{f}</span>)}</div>
                )}

                {rawRows.length > 0 && (
                    <>
                        <div className='df gap1 fw mt2 mb2'>
                            {FIELDS.map(f => (
                                <div key={f.key} className='flex1' style={{ minWidth: '140px' }}>
                                    <label className='fr cw fs12'>{f.label}</label>
                                    <select className='ub-select mt1' value={map[f.key]} onChange={(e) => { const v = e.currentTarget.value; setMap(m => ({ ...m, [f.key]: v })) }}>
                                        <option value=''>—</option>
                                        {headers.map(h => <option key={h} value={h}>{h}</option>)}
                                    </select>
                                </div>
                            ))}
                        </div>

                        <div className='df aic jcsb fw gap1 mb2'>
                            <p className='cw fr fs12' style={{ opacity: 0.7 }}>{merged.length} user(s) unique(s) · {selectedCount} sélectionné(s) · {saved} enregistré(s)</p>
                            <button onClick={saveSelected} disabled={saving || !map.email || selectedCount === 0} className='btn cs df aic jcc gap1'>
                                {saving ? <span className='loader sm'></span> : <><Database size={16} color='#fff' /> Envoyer la sélection ({selectedCount})</>}
                            </button>
                        </div>

                        <div className='ub-scroll'>
                            <div className='ub-table merged'>
                                <div className='ub-head'>
                                    <span className='tac'><input type='checkbox' checked={allSelected} onChange={toggleAll} /></span>
                                    <span>Email</span><span>Nom</span>
                                    <span className='tac'>Paiements</span><span className='tar'>Total €</span>
                                    <span className='tac'>Mois</span><span>Début</span>
                                    <span className='tac'>Reste</span><span className='tac'>Fin premium</span>
                                    <span className='tac'>Statut</span>
                                </div>
                                {merged.map((m, i) => {
                                    const key = emailKey(m.email)
                                    return (
                                        <div className={`ub-row ${isSel(m.email) ? 'sel' : ''}`} key={i}>
                                            <span className='tac'><input type='checkbox' checked={isSel(m.email)} onChange={() => toggleSel(m.email)} /></span>
                                            <span className='cw truncate'>{m.email}</span>
                                            <span className='cw truncate'>{m.name}</span>
                                            <span className='tac cw'>{m.count}</span>
                                            <span className='tar cw fb'>{m.euro.toFixed(2)}€</span>
                                            <span className='tac cw fb'>{m.months}</span>
                                            <span className='cw fs12'>{m.firstStr ? moment(m.firstStr).format('DD/MM/YY') : '—'}</span>
                                            <span className='tac'><span className={`ub-premium ${m.premium ? 'ok' : 'ko'}`}>{m.remaining}</span></span>
                                            <span className='tac cw fs12'>{m.end && m.end.isValid() ? m.end.format('DD/MM/YY') : '—'}</span>
                                            <span className='tac ub-status'>
                                                {status[key] === 'loading' && <span className='loader sm'></span>}
                                                {status[key] === 'ok' && <Check size={16} color='#15992f' />}
                                                {status[key] === 'err' && <X size={16} color='#f71717' />}
                                            </span>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {pending && (
                <div className='modal-overlay' onClick={() => setPending(null)}>
                    <div className='modal-box' onClick={(e) => e.stopPropagation()}>
                        <p className='fb cw fs18 mb1'>Devise du fichier</p>
                        <p className='fr cw fs12 mb2' style={{ opacity: 0.6 }}>{pending.name}</p>
                        <div className='df gap1'>
                            <button onClick={() => confirmCurrency('PLN')} className='btn cs flex1'>Zloty (PLN)</button>
                            <button onClick={() => confirmCurrency('EUR')} className='btn cs flex1'>Euro (EUR)</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default UserBackupImport;
